import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { projectsAPI, paymentsAPI } from '../services/api'
import { ArrowLeft, CreditCard, Euro, Clock, CheckCircle, AlertCircle } from 'lucide-react'

const ProjectPaymentPage = () => {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [project, setProject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [paying, setPaying] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchProject()
  }, [projectId])

  const fetchProject = async () => {
    try {
      setLoading(true)
      const response = await projectsAPI.getProject(projectId)
      setProject(response.data.project)
    } catch (error) {
      console.error('Error fetching project:', error)
      setError('Fout bij het laden van het project')
    } finally {
      setLoading(false)
    }
  }

  const handleDepositPayment = async () => {
    try {
      setPaying(true)
      setError('')

      // Bedrag wordt server-side berekend uit project.payment.deposit (10% incl. BTW);
      // we sturen géén amount/method meer mee.
      const paymentResponse = await paymentsAPI.processDepositPayment(projectId)

      if (paymentResponse.data && paymentResponse.data.data) {
        // Redirect to Stripe Checkout
        const checkoutUrl = paymentResponse.data.data.checkoutUrl;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          setError('Betaal URL niet ontvangen. Probeer het opnieuw.');
        }
      }
    } catch (error) {
      console.error('Payment error:', error)
      const data = error?.response?.data
      if (data?.billingIncomplete) {
        alert(data.message)
        navigate('/profile?tab=billing')
        return
      }
      setError(data?.message || 'Betaling mislukt. Probeer het opnieuw.')
    } finally {
      setPaying(false)
    }
  }

  const onPaymentSuccess = async () => {
    setShowPayment(false)
    await fetchProject()
    alert('Betaling succesvol! Je project is nu live op de marktplaats.')
    navigate('/my-projects')
  }

  const onPaymentError = (error) => {
    console.error('Payment failed:', error)
    setError('Betaling mislukt. Probeer het opnieuw.')
    setShowPayment(false)
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="font-['Inter'] min-h-screen flex items-center justify-center bg-white text-[#0F172A]">
        <div className="w-8 h-8 border-2 border-[#1D8BF8] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error && !project) {
    return (
      <div className="font-['Inter'] min-h-screen flex items-center justify-center bg-white text-[#0F172A]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-xl mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-[#3EB489] hover:bg-[#359E78] rounded-lg transition text-white"
          >
            Terug
          </button>
        </div>
      </div>
    )
  }

  // Aanbetaling = 10% incl. 21% BTW, eindbetaling = resterende 90% incl. BTW.
  const depositAmountExcl = project.payment?.deposit?.amount || project.budget * 0.10
  const depositBtw = project.payment?.deposit?.btw || Math.round(depositAmountExcl * 0.21 * 100) / 100
  const depositAmountIncBtw = project.payment?.deposit?.amountIncBtw || Math.round(depositAmountExcl * 1.21 * 100) / 100
  const finalAmountIncBtw = project.payment?.final?.amountIncBtw || Math.round(project.budget * 0.90 * 1.21 * 100) / 100
  const totalBudget = project.budget
  const totalIncBtw = Math.round(totalBudget * 1.21 * 100) / 100
  const isDepositPaid = project.payment?.deposit?.status === 'paid'

  return (
    <div className="font-['Inter'] min-h-screen flex flex-col bg-white text-[#0F172A]">

      {/* Header */}
      <header className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 p-2 -ml-2 rounded-lg min-h-[44px] text-[#475569] hover:text-[#0F172A] hover:bg-gray-50 transition flex-shrink-0"
            aria-label="Terug"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Terug</span>
          </button>
          <div className="hidden sm:block w-px h-6 bg-gray-200"></div>
          <Link to="/dashboard" className="text-2xl font-black tracking-tighter text-[#0F172A] whitespace-nowrap">
            UGC<span className="text-[#1D8BF8]">4you</span>
          </Link>
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap ${
            (project.status === 'awaiting_deposit' || project.status === 'proposal_accepted')
              ? 'bg-orange-50 text-orange-600'
              : 'bg-emerald-50 text-emerald-600'
          }`}>
            {(project.status === 'awaiting_deposit' || project.status === 'proposal_accepted') ? 'Wacht op aanbetaling' : 'Betaald'}
          </span>
        </div>
      </header>

      {/* Stripe Payment Modal */}

      {/* Main Content */}
      <div className="flex-1 relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Project Header */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 break-words">{project.title}</h1>
            <p className="text-[#0F172A] text-lg">Voltooi je betaling om je project live te zetten</p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-red-500 mr-3" />
                <p className="text-sm text-red-500">{error}</p>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Payment Overview */}
            <div className="bg-[#F8FAFC] backdrop-blur rounded-2xl p-5 sm:p-8 border border-gray-200 shadow-md">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Euro className="w-6 h-6 text-emerald-600" />
                Betalingsoverzicht
              </h2>

              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-4 bg-[#F8FAFC] rounded-lg">
                  <div className="min-w-0">
                    <h3 className="font-semibold">Totaal project</h3>
                    <p className="text-sm text-[#475569]">10% aanbetaling nu, 90% na goedkeuring</p>
                  </div>
                  <div className="sm:text-right flex-shrink-0">
                    <div className="text-2xl font-bold whitespace-nowrap">{formatCurrency(totalIncBtw)}</div>
                    <p className="text-xs text-[#475569]">incl. BTW</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className={`p-4 rounded-lg border ${
                    isDepositPaid
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-yellow-50 border-yellow-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {isDepositPaid ? (
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <Clock className="w-5 h-5 text-yellow-600" />
                      )}
                      <h3 className="font-semibold">Aanbetaling (10%)</h3>
                    </div>
                    <div className="text-xl font-bold whitespace-nowrap">{formatCurrency(depositAmountIncBtw)}</div>
                    <p className="text-xs text-[#475569] mt-1">
                      {isDepositPaid ? 'Betaald' : 'Te betalen nu'} (incl. 21% BTW)
                    </p>
                  </div>

                  <div className="p-4 bg-[#F8FAFC] rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-5 h-5 text-[#475569]/50" />
                      <h3 className="font-semibold">Eindbetaling (90%)</h3>
                    </div>
                    <div className="text-xl font-bold whitespace-nowrap">{formatCurrency(finalAmountIncBtw)}</div>
                    <p className="text-xs text-[#475569] mt-1">
                      Na goedkeuring content (incl. 21% BTW)
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <h3 className="font-semibold mb-2">Wat gebeurt er na betaling?</h3>
                  <ul className="space-y-2 text-sm text-[#0F172A]">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      Je project wordt zichtbaar voor creators
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      Creators kunnen voorstellen indienen
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      Je kunt een creator selecteren
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      Werk kan beginnen
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Payment Action */}
            <div className="bg-[#F8FAFC] backdrop-blur rounded-2xl p-5 sm:p-8 border border-gray-200 shadow-md">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <CreditCard className="w-6 h-6 text-[#1D8BF8]" />
                Betaling
              </h2>

              {isDepositPaid ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Betaling Voltooid!</h3>
                  <p className="text-[#0F172A] mb-6">
                    Je project is nu live en zichtbaar voor creators.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/my-projects"
                      className="flex-1 px-6 py-3 bg-[#3EB489] hover:bg-[#359E78] rounded-lg transition text-center font-medium text-white"
                    >
                      Naar Mijn Projecten
                    </Link>
                    <Link
                      to="/projects"
                      className="flex-1 px-6 py-3 bg-gray-100 hover:bg-[#F8FAFC] rounded-lg transition text-center font-medium"
                    >
                      Bekijk Live Project
                    </Link>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="bg-blue-50 rounded-lg p-6 mb-6">
                    <h3 className="text-xl font-bold mb-2">Betaal Aanbetaling</h3>
                    <p className="text-[#0F172A] mb-4">
                      Betaal {formatCurrency(depositAmountIncBtw)} (10% aanbetaling incl. BTW) om je creator vast te leggen en de workspace te openen
                    </p>
                    <div className="text-2xl sm:text-3xl font-black text-[#1D8BF8] break-words">
                      {formatCurrency(depositAmountIncBtw)}
                    </div>
                    <div className="mt-2 text-sm text-[#475569] space-y-1">
                      <div className="flex justify-between">
                        <span>Subtotaal (10%)</span>
                        <span>{formatCurrency(depositAmountExcl)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-[#475569]">
                        <span>BTW 21%</span>
                        <span>{formatCurrency(depositBtw)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-[#0F172A] pt-1 border-t border-gray-200">
                        <span>Totaal incl. BTW</span>
                        <span>{formatCurrency(depositAmountIncBtw)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6">
                    <p className="text-sm font-semibold text-emerald-700 mb-1">🔒 Veilig via escrow</p>
                    <p className="text-sm text-[#475569] leading-relaxed">
                      Je aanbetaling staat veilig vast op onze escrow-rekening. Het geld gaat <strong className="text-[#0F172A]">pas naar de creator nadat jíj de content hebt goedgekeurd</strong>. De resterende 90% reken je daarna pas af.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <button
                      onClick={handleDepositPayment}
                      disabled={paying}
                      className="w-full px-6 py-4 bg-[#3EB489] hover:bg-[#359E78] disabled:opacity-50 rounded-lg transition font-bold text-lg flex items-center justify-center gap-3 text-white"
                    >
                      {paying ? (
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <CreditCard className="w-5 h-5" />
                      )}
                      {paying ? 'Bezig met betalen...' : 'Betaal met iDEAL'}
                    </button>

                    <p className="text-xs text-[#475569] text-center">
                      Veilig betalen via iDEAL. Je wordt doorgeleid naar je bank.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProjectPaymentPage
